import { Link } from "react-router-dom";
import type { LucideIcon } from "lucide-react";
import { Check, EllipsisVertical } from "lucide-react";
import { motion } from "framer-motion";
import type { ReactNode } from "react";
import { cn } from "@/shared/lib/cn";
import { motionTokens, riseItem, springs, staggerContainer } from "@/shared/lib/motion";
import { AnimatedNumber } from "@/shared/ui/AnimatedNumber";

export type KpiTone = "purple" | "mint" | "pink" | "sky" | "amber" | "red" | "lime" | "slate";

/** Dégradé de la carte, bordure, pastille de l'icône, filigrane, jauge et contour actif de chaque teinte. */
export const KPI_TONES: Record<KpiTone, { card: string; chip: string; watermark: string; meter: string; ring: string }> = {
  purple: {
    card: "border-violet-200/80 bg-[linear-gradient(135deg,#ffffff_0%,#ece2fc_100%)] dark:border-violet-500/20 dark:bg-[linear-gradient(135deg,#132a2f_0%,#2c2244_100%)]",
    chip: "bg-violet-200 text-violet-700 dark:bg-violet-500/25 dark:text-violet-300",
    watermark: "text-violet-300",
    meter: "bg-violet-500",
    ring: "ring-violet-400/60",
  },
  mint: {
    card: "border-emerald-200/80 bg-[linear-gradient(135deg,#ffffff_0%,#d5f5e3_100%)] dark:border-emerald-500/20 dark:bg-[linear-gradient(135deg,#132a2f_0%,#14382f_100%)]",
    chip: "bg-emerald-200 text-emerald-700 dark:bg-emerald-500/25 dark:text-emerald-300",
    watermark: "text-emerald-300",
    meter: "bg-emerald-500",
    ring: "ring-emerald-400/60",
  },
  pink: {
    card: "border-rose-200/80 bg-[linear-gradient(135deg,#ffffff_0%,#fbdde4_100%)] dark:border-rose-500/20 dark:bg-[linear-gradient(135deg,#132a2f_0%,#3a1f2b_100%)]",
    chip: "bg-rose-200 text-rose-700 dark:bg-rose-500/25 dark:text-rose-300",
    watermark: "text-rose-300",
    meter: "bg-rose-500",
    ring: "ring-rose-400/60",
  },
  sky: {
    card: "border-sky-200/80 bg-[linear-gradient(135deg,#ffffff_0%,#d8e9fc_100%)] dark:border-sky-500/20 dark:bg-[linear-gradient(135deg,#132a2f_0%,#163149_100%)]",
    chip: "bg-sky-200 text-blue-700 dark:bg-sky-500/25 dark:text-sky-300",
    watermark: "text-sky-300",
    meter: "bg-blue-500",
    ring: "ring-sky-400/60",
  },
  amber: {
    card: "border-amber-200/80 bg-[linear-gradient(135deg,#ffffff_0%,#fdeec8_100%)] dark:border-amber-500/20 dark:bg-[linear-gradient(135deg,#132a2f_0%,#3a3119_100%)]",
    chip: "bg-amber-200 text-amber-700 dark:bg-amber-500/25 dark:text-amber-300",
    watermark: "text-amber-300",
    meter: "bg-amber-500",
    ring: "ring-amber-400/60",
  },
  red: {
    card: "border-red-200/80 bg-[linear-gradient(135deg,#ffffff_0%,#fbdcdc_100%)] dark:border-red-500/20 dark:bg-[linear-gradient(135deg,#132a2f_0%,#3d1d20_100%)]",
    chip: "bg-red-200 text-red-700 dark:bg-red-500/25 dark:text-red-300",
    watermark: "text-red-300",
    meter: "bg-red-500",
    ring: "ring-red-400/60",
  },
  lime: {
    card: "border-lime-200/80 bg-[linear-gradient(135deg,#ffffff_0%,#e6f8c4_100%)] dark:border-lime-500/20 dark:bg-[linear-gradient(135deg,#132a2f_0%,#24391f_100%)]",
    chip: "bg-accent text-accent-foreground",
    watermark: "text-lime-300",
    meter: "bg-accent",
    ring: "ring-lime-500/60",
  },
  slate: {
    card: "border-slate-200 bg-[linear-gradient(135deg,#ffffff_0%,#e3ebef_100%)] dark:border-white/10 dark:bg-[linear-gradient(135deg,#132a2f_0%,#1d3035_100%)]",
    chip: "bg-slate-200 text-primary dark:bg-white/15 dark:text-white",
    watermark: "text-slate-300",
    meter: "bg-primary",
    ring: "ring-primary/40",
  },
};

const formatCount = (value: number) => Math.round(value).toLocaleString("fr-FR");

/** Grille d'indicateurs : liste de définitions dont les cartes apparaissent l'une après l'autre. */
export function KpiGrid({ className, children }: { className?: string; children: ReactNode }) {
  return (
    <motion.dl className={cn("grid gap-4", className)} variants={staggerContainer} initial="hidden" animate="visible">
      {children}
    </motion.dl>
  );
}

type KpiCardProps = {
  tone: KpiTone;
  icon: LucideIcon;
  label: string;
  /** Un nombre défile jusqu'à sa valeur ; un texte s'affiche tel quel. Ignoré si `children` est fourni. */
  value?: number | string;
  unit?: string;
  hint?: ReactNode;
  watermark?: LucideIcon;
  /** Toute la carte mène à cette page. */
  href?: string;
  /** Toute la carte agit comme un filtre ; `active` indique le filtre appliqué. */
  onClick?: () => void;
  active?: boolean;
  className?: string;
  children?: ReactNode;
};

/**
 * Carte d'indicateur : pastille d'icône, libellé, valeur en grand et ligne d'aide, sur un dégradé clair de sa teinte.
 * À placer dans une `KpiGrid`.
 */
export function KpiCard({
  tone,
  icon: Icon,
  label,
  value,
  unit,
  hint,
  watermark: Watermark,
  href,
  onClick,
  active = false,
  className,
  children,
}: KpiCardProps) {
  const styles = KPI_TONES[tone];
  const interactive = Boolean(href || onClick);
  // Lien ou bouton étiré sur toute la carte : la carte reste un enfant valide de la <dl>.
  const overlayClass =
    "absolute inset-0 z-10 rounded-[inherit] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40";

  return (
    <motion.div
      variants={riseItem}
      whileHover={interactive ? { y: -motionTokens.distance.xs } : undefined}
      transition={springs.snappy}
      className={cn(
        "relative isolate flex flex-col gap-4 overflow-hidden rounded-[var(--radius-card)] border p-5 shadow-[var(--shadow-card)]",
        styles.card,
        active && cn("ring-2", styles.ring),
        className,
      )}
    >
      {Watermark ? (
        <Watermark
          size={64}
          strokeWidth={1.5}
          className={cn("pointer-events-none absolute bottom-5 right-5 -z-10 opacity-60 dark:opacity-25", styles.watermark)}
          aria-hidden="true"
        />
      ) : null}
      <div className="flex items-center justify-between gap-2">
        <dt className="flex min-w-0 items-center gap-3 text-sm font-semibold text-foreground">
          <span className={cn("grid h-10 w-10 shrink-0 place-items-center rounded-xl", styles.chip)}>
            <Icon size={19} strokeWidth={2} aria-hidden="true" />
          </span>
          <span className="min-w-0 text-pretty">{label}</span>
        </dt>
        {href ? (
          <span className="grid h-8 w-8 shrink-0 place-items-center rounded-lg text-muted-foreground" aria-hidden="true">
            <EllipsisVertical size={18} />
          </span>
        ) : null}
        {onClick && active ? (
          <span className={cn("grid h-6 w-6 shrink-0 place-items-center rounded-full", styles.chip)} aria-hidden="true">
            <Check size={14} strokeWidth={2.5} />
          </span>
        ) : null}
      </div>
      <dd className="flex flex-1 flex-col justify-end gap-2">
        {children ?? (
          <>
            <p className="break-words text-[1.75rem] font-bold leading-none tracking-tight text-foreground">
              {typeof value === "number" ? <AnimatedNumber value={value} format={formatCount} /> : (value ?? "—")}
              {unit ? <span className="ml-2 text-lg font-bold">{unit}</span> : null}
            </p>
            {hint ? <p className="text-sm text-muted-foreground">{hint}</p> : null}
          </>
        )}
      </dd>

      {href ? <Link to={href} aria-label={`Voir le détail : ${label}`} className={overlayClass} /> : null}
      {onClick ? (
        <button type="button" onClick={onClick} aria-pressed={active} aria-label={`Filtrer : ${label}`} className={overlayClass} />
      ) : null}
    </motion.div>
  );
}

/** Jauge horizontale d'une carte d'indicateur (pourcentage de 0 à 100). */
export function KpiMeter({ value, className }: { value: number; className: string }) {
  return (
    <span className="block h-2 flex-1 overflow-hidden rounded-full bg-black/5 dark:bg-white/10" aria-hidden="true">
      <motion.span
        className={cn("block h-full origin-left rounded-full", className)}
        style={{ width: `${Math.min(100, Math.max(2, value))}%` }}
        initial={{ scaleX: 0 }}
        animate={{ scaleX: 1 }}
        transition={{ duration: motionTokens.duration.crawl, ease: motionTokens.easing.smooth, delay: 0.2 }}
      />
    </span>
  );
}
